import { useEffect, useLayoutEffect } from 'react';

// pmax_early_shell_* marks recorded across interactive.ts (pre-React) and useEarlyShellHandoff
// (post-mount), paired with the ActivityMarker/EarlyShell.* name each is logged under once
// flushed by useEarlyShellPerfLogging below.
const EARLY_SHELL_MARKS = [
  ['pmax_early_shell_shown', 'ActivityMarker/EarlyShell.Shown'],
  ['pmax_early_shell_first_input', 'ActivityMarker/EarlyShell.FirstInput'],
  ['pmax_early_shell_react_ready', 'ActivityMarker/EarlyShell.ReactReady'],
  ['pmax_early_shell_handoff_complete', 'ActivityMarker/EarlyShell.HandoffComplete'],
];

// Runs once on mount when the early shell is enabled. Hides the static placeholders and
// reveals #root — no overlay, no fade.
//
// DOM order: #static-early-shell is BEFORE #root. If we just removed body.es-active, #root
// would render below the shell, pushing the real header to the bottom.
//
// Fix: transition the shell to a position:fixed transparent overlay (out of normal flow).
// #root then renders at the top naturally — React header at top, footer at bottom. The card
// floats at the same viewport position (still at 20vh via padding-top). pointer-events:none
// on the shell lets clicks reach the React header/footer around the card. The main content remains
// the scroll container so its scrollbar starts below the header. Scrollbar dragging is handled
// from document-level mouse events because a native scrollbar cannot receive a pointer while its
// parent shell is click-through. pointer-events:auto on the card/footer (not the full-width
// contentEl) keeps them interactive while leaving the empty side margins click-through —
// otherwise contentEl's full-width invisible box would swallow clicks meant for anything else
// rendered in that space, e.g. a live-chat side panel.
//
// Time-critical only: the DOM mutations and the react_ready/handoff_complete marks must land
// in useLayoutEffect (pre-paint) — that's the whole point of using it instead of useEffect (see
// PR review history). Actually logging those marks is not time-critical and would otherwise
// hold up paint for no visual benefit, so it's split out into useEarlyShellPerfLogging below,
// which runs in a plain (post-paint) useEffect.
export const useEarlyShellHandoff = (isEarlyShellEnabled) => {
  useLayoutEffect(() => {
    if (!isEarlyShellEnabled) { return undefined; }

    const shellEl = document.getElementById('static-early-shell');
    const contentEl = document.getElementById('es-shell-content');
    let scrollbarDrag;
    let headerObserver;
    let couponBannerObserver;
    const isInScrollbarGutter = (event) => {
      if (!contentEl?.isConnected || contentEl.scrollHeight <= contentEl.clientHeight) { return false; }

      const scrollbarWidth = Math.max(contentEl.offsetWidth - contentEl.clientWidth, 16);
      const contentRect = contentEl.getBoundingClientRect();
      const withinContentHeight = event.clientY >= contentRect.top && event.clientY <= contentRect.bottom;
      const withinScrollbarWidth = shellEl.dir === 'rtl'
        ? event.clientX >= contentRect.left && event.clientX <= contentRect.left + scrollbarWidth
        : event.clientX >= contentRect.right - scrollbarWidth && event.clientX <= contentRect.right;
      return withinContentHeight && withinScrollbarWidth;
    };
    const startScrollbarDrag = (event) => {
      if (event.button !== 0 || !isInScrollbarGutter(event)) { return; }

      const maxScrollTop = contentEl.scrollHeight - contentEl.clientHeight;
      const thumbHeight = (contentEl.clientHeight * contentEl.clientHeight) / contentEl.scrollHeight;
      const thumbTravel = contentEl.clientHeight - thumbHeight;
      scrollbarDrag = {
        startClientY: event.clientY,
        startScrollTop: contentEl.scrollTop,
        scrollPerPixel: maxScrollTop / thumbTravel,
      };
      event.preventDefault();
    };
    const continueScrollbarDrag = (event) => {
      if (!scrollbarDrag || !contentEl?.isConnected) { return; }

      contentEl.scrollTop = scrollbarDrag.startScrollTop
        + ((event.clientY - scrollbarDrag.startClientY) * scrollbarDrag.scrollPerPixel);
      event.preventDefault();
    };
    const stopScrollbarDrag = () => {
      scrollbarDrag = undefined;
    };
    const forwardMainWheel = (event) => {
      if (!contentEl?.isConnected || contentEl.contains(event.target) || !event.deltaY) { return; }

      const contentRect = contentEl.getBoundingClientRect();
      const withinContent = event.clientX >= contentRect.left && event.clientX <= contentRect.right
        && event.clientY >= contentRect.top && event.clientY <= contentRect.bottom;
      if (!withinContent) { return; }

      let deltaMultiplier = 1;
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        deltaMultiplier = 16;
      } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        deltaMultiplier = contentEl.clientHeight;
      }
      const previousScrollTop = contentEl.scrollTop;
      contentEl.scrollTop += event.deltaY * deltaMultiplier;
      if (contentEl.scrollTop !== previousScrollTop) { event.preventDefault(); }
    };
    if (shellEl) {
      shellEl.style.position = 'fixed';
      // top/left/right only — `bottom` is owned by useReserveTailSpace, which pulls the
      // shell's own bottom edge above the fixed inline-support bar.
      shellEl.style.top = '0';
      shellEl.style.left = '0';
      shellEl.style.right = '0';
      shellEl.style.backgroundColor = 'transparent';
      shellEl.style.backgroundImage = 'none';
      shellEl.style.zIndex = '10';
      shellEl.style.pointerEvents = 'none';
      shellEl.style.overflowY = 'hidden';
      document.addEventListener('mousedown', startScrollbarDrag, true);
      document.addEventListener('mousemove', continueScrollbarDrag, true);
      document.addEventListener('mouseup', stopScrollbarDrag, true);
      document.addEventListener('wheel', forwardMainWheel, { passive: false });
    }
    const cardContainerEl = document.getElementById('es-card-container');
    if (cardContainerEl) { cardContainerEl.style.pointerEvents = 'auto'; }
    const footerSlotEl = document.getElementById('es-footer-slot');
    if (footerSlotEl) { footerSlotEl.style.pointerEvents = 'auto'; }

    const hdrPh = document.getElementById('es-header-temp');
    const hideHeaderPlaceholder = () => {
      if (!document.querySelector('.app-shell-header > *')) { return false; }

      // visibility, not display:none — hides it but keeps its 60px of space, so the card below
      // doesn't jump up.
      hdrPh.style.visibility = 'hidden';
      headerObserver?.disconnect();
      return true;
    };
    if (hdrPh && !hideHeaderPlaceholder()) {
      headerObserver = new MutationObserver(hideHeaderPlaceholder);
      headerObserver.observe(document.body, { childList: true, subtree: true });
    }
    const couponBannerPh = document.getElementById('es-coupon-banner');
    const hideCouponBannerPlaceholder = () => {
      if (!document.querySelector('.promotion-banner .alert-coupon')) { return false; }

      couponBannerPh.style.visibility = 'hidden';
      couponBannerObserver?.disconnect();
      return true;
    };
    // Coupon state and layout expansion settle in effects after this handoff. Keep the static
    // banner visible until the real banner mounts; MutationObserver runs before the next paint,
    // avoiding a blank frame while retaining the placeholder's layout space.
    if (couponBannerPh && couponBannerPh.style.display !== 'none' && !hideCouponBannerPlaceholder()) {
      couponBannerObserver = new MutationObserver(hideCouponBannerPlaceholder);
      couponBannerObserver.observe(document.body, { childList: true, subtree: true });
    }

    document.body.classList.remove('es-active');
    performance.mark('pmax_early_shell_react_ready');
    performance.mark('pmax_early_shell_handoff_complete');
    try {
      performance.measure('pmax_early_shell_duration', 'pmax_early_shell_shown', 'pmax_early_shell_handoff_complete');
    } catch (e) { /* ignore */ }
    return () => {
      document.removeEventListener('mousedown', startScrollbarDrag, true);
      document.removeEventListener('mousemove', continueScrollbarDrag, true);
      document.removeEventListener('mouseup', stopScrollbarDrag, true);
      document.removeEventListener('wheel', forwardMainWheel);
      headerObserver?.disconnect();
      couponBannerObserver?.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — run once on mount only
};

// Logs every pmax_early_shell_* mark through the real instrumentation pipeline, once React is
// ready — mirrors index.jsx's handling of window.performance.preAppStartMarker. Deliberately a
// plain useEffect (runs after paint), not useLayoutEffect: reading marks back and calling
// activity.perf() is pure reporting, not visual, so it must never hold up the handoff's paint.
// No separate marker object is captured anywhere: each mark's own PerformanceMark entry
// (already written by performance.mark() — including the pre-React ones from interactive.ts and
// the ones useEarlyShellHandoff just wrote) is read back via getEntriesByName() for its
// timestamp, so there's nothing to duplicate.
export const useEarlyShellPerfLogging = (isEarlyShellEnabled, activity) => {
  useEffect(() => {
    if (!isEarlyShellEnabled || !activity || typeof activity.perf !== 'function') { return; }

    const activeTimeAtFlush = typeof window.performance.activeTime === 'function'
      ? window.performance.activeTime() : NaN;
    EARLY_SHELL_MARKS.forEach(([markName, eventName]) => {
      const [entry] = performance.getEntriesByName(markName, 'mark');
      if (entry) {
        activity.perf({
          eventName,
          eventStartTime: entry.startTime,
          activeTimeBeforeEventStart: activeTimeAtFlush,
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — run once on mount only
};
